/* Add and edit items — where adoption is won or lost.
 *
 * Someone is entering thirty doors off a cabinetmaker's schedule, so the
 * primary action is **Duplicate last row**, not "Add". Component type,
 * substrate, edge, colour and finish are pickers over admin reference data.
 * Numeric fields carry inputmode="numeric" so a phone or iPad opens a keypad,
 * and every input is 16px so iOS doesn't zoom on focus.
 *
 * The list is a frame, not a page: the card fills what is left of the screen,
 * the cut list scrolls inside it with its headings pinned, and the totals and
 * Save stay on screen however many lines the job runs to. A line that has just
 * been added is scrolled to and flashed once, because sixty rows down the
 * bottom of the list is nowhere near where you were looking. */
import type { CSSProperties } from 'react';
import { useEffect, useRef } from 'react';
import { sqmOf } from '@/domain/items';
import { currentJob, useApp } from '@/store/useApp';
import { NO_AUTOFILL } from '@/ui/autofill';
import { BackButton } from '@/ui/controls';
import { Swatch } from '@/ui/Swatch';
import { NoJobOpen } from './JobCard';
import { usePageGutter, withGutter } from '@/ui/viewport';

const pickerStyle: CSSProperties = {
  minHeight: 40,
  padding: '5px 8px',
  borderRadius: 8,
  whiteSpace: 'nowrap',
  background: 'transparent',
  border: '1px solid transparent',
  cursor: 'pointer',
  fontSize: 13.5,
  textAlign: 'left',
  color: 'var(--ink)',
};

const numStyle: CSSProperties = {
  width: 66,
  minHeight: 40,
  borderRadius: 8,
  textAlign: 'right',
  fontFamily: 'var(--font-data)',
  fontSize: 13,
  border: '1px solid var(--line-soft)',
  background: 'var(--surface)',
  padding: '0 6px',
};

/** Strip anything that isn't a digit — the field takes a paste from a
 *  spreadsheet as happily as a keypad. */
const digits = (v: string) => {
  const n = parseInt(v.replace(/[^0-9]/g, ''), 10);
  return Number.isNaN(n) ? 0 : n;
};

export function ItemsScreen() {
  const gutter = usePageGutter();
  const st = useApp();
  const job = currentJob(st);

  const totalQty = st.rows.reduce((a, r) => a + r.qty, 0);
  const totalArea = st.rows.reduce((a, r) => a + sqmOf(r), 0);
  // Two ways back, and they mean different things. The link at the top is
  // navigation within one job — the job sheet's Items tab is this same working
  // set, so nothing is lost by going up to it and nothing needs asking.
  // Cancel abandons the edits, so it goes through the guard: it is the one
  // control here that throws the cut list away.
  const backToJob = () => st.set('screen', 'job');
  const cancel = () => st.leave(backToJob);

  const { justAdded, clearJustAdded } = st;
  const addedRow = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (justAdded === null) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    addedRow.current?.scrollIntoView?.({
      block: 'nearest',
      behavior: reduced ? 'auto' : 'smooth',
    });
    // Long enough for the flash to finish; after that the row is just a row.
    const done = window.setTimeout(clearJustAdded, 1900);
    return () => window.clearTimeout(done);
  }, [justAdded, clearJustAdded]);

  // A cut list belongs to a job, and there is a moment on a signed-in boot
  // before the board has answered — and a shop on its first morning that has
  // no jobs at all. Below the hooks, because the hooks have to run either way.
  if (!job) return <NoJobOpen />;

  return (
    <section
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: withGutter(gutter, 20, 24),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 16,
          marginBottom: 16,
          flex: 'none',
        }}
      >
        <BackButton
          to={job.code}
          onClick={backToJob}
          style={{ marginLeft: -8, alignSelf: 'center' }}
        />
        <div>
          <h1 style={{ fontSize: 26, margin: '0 0 2px' }}>Items · {job.code}</h1>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-muted)' }}>
            {job.title} — entering from the cabinetmaker&rsquo;s door schedule.
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={st.addRow}
            style={{ height: 48 }}
          >
            Add row
          </button>
          <button
            type="button"
            className="btn btn-primary blueprint"
            onClick={st.duplicateLast}
            style={{ height: 48, paddingInline: 18 }}
          >
            Duplicate last row
          </button>
        </div>
      </div>

      {st.sel.length > 0 ? (
        <div
          className="blueprint"
          style={{
            background: 'var(--accent-tint)',
            padding: '12px 14px',
            marginBottom: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flex: 'none',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            {st.sel.length} {st.sel.length === 1 ? 'row' : 'rows'} selected
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => st.openPicker('colour', null)}
            style={{ height: 40, background: 'var(--surface)' }}
          >
            Set colour
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => st.openPicker('finish', null)}
            style={{ height: 40, background: 'var(--surface)' }}
          >
            Set finish
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={st.deleteSelected}
            style={{ height: 40, background: 'var(--surface)', color: 'var(--urgent)' }}
          >
            Delete rows
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={st.clearSelection}
            style={{ marginLeft: 'auto' }}
          >
            Clear selection
          </button>
        </div>
      ) : null}

      <div
        className="blueprint"
        style={{
          background: 'var(--surface)',
          padding: 0,
          flex: '1 1 auto',
          minHeight: 320,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* The one thing that scrolls on this screen. `minHeight: 0` is what
            lets it shrink inside the card rather than growing past the fold. */}
        <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
          <table
            className="table table-sticky"
            style={{ width: '100%', minWidth: 1210, borderCollapse: 'collapse' }}
          >
            <thead>
              <tr>
                <th style={{ width: 42, paddingLeft: 14 }}>
                  <span className="sr-only">Select</span>
                </th>
                <th style={{ width: 46, textAlign: 'right' }}>Item</th>
                <th>Component</th>
                <th>Substrate</th>
                <th style={{ textAlign: 'right' }}>W</th>
                <th style={{ textAlign: 'right' }}>H</th>
                <th>Faces</th>
                <th>Edge</th>
                <th>Colour</th>
                <th>Finish</th>
                <th style={{ textAlign: 'right' }}>Coats</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>m²</th>
                <th style={{ width: 80, paddingRight: 14 }} />
              </tr>
            </thead>
            <tbody>
              {st.rows.map((r, i) => {
                const selected = st.sel.includes(r.id);
                const isNew = r.id === justAdded;
                return (
                  <tr
                    key={r.id}
                    ref={isNew ? addedRow : undefined}
                    className={isNew ? 'row-new' : undefined}
                    style={{
                      background: selected
                        ? 'var(--accent-tint)'
                        : i % 2
                          ? 'var(--surface-2)'
                          : 'transparent',
                    }}
                  >
                    <td style={{ paddingLeft: 14 }}>
                      <button
                        type="button"
                        aria-label={`Select item ${r.no}`}
                        aria-pressed={selected}
                        onClick={() => st.toggleSelected(r.id)}
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 7,
                          border: `1px solid ${selected ? 'var(--accent)' : 'var(--dim)'}`,
                          background: selected ? 'var(--accent)' : 'var(--surface)',
                          color: 'var(--on-accent)',
                          fontFamily: 'var(--font-data)',
                          fontSize: 13,
                          lineHeight: 1,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {selected ? '×' : ''}
                      </button>
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontFamily: 'var(--font-data)',
                        fontSize: 12.5,
                        color: 'var(--ink-muted)',
                      }}
                    >
                      {r.no}
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => st.openPicker('type', r.id)}
                        style={pickerStyle}
                      >
                        {r.type}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => st.openPicker('substrate', r.id)}
                        style={pickerStyle}
                      >
                        {r.substrate}
                      </button>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        inputMode="numeric"
                        autoComplete={NO_AUTOFILL}
                        aria-label="Width in mm"
                        value={r.w}
                        onChange={(e) => st.setRow(r.id, { w: digits(e.target.value) })}
                        style={numStyle}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        inputMode="numeric"
                        autoComplete={NO_AUTOFILL}
                        aria-label="Height in mm"
                        value={r.h}
                        onChange={(e) => st.setRow(r.id, { h: digits(e.target.value) })}
                        style={numStyle}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => st.setRow(r.id, { faces: r.faces === 2 ? 1 : 2 })}
                        style={pickerStyle}
                      >
                        {r.faces === 2 ? '2 faces' : '1 face'}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => st.openPicker('edge', r.id)}
                        style={pickerStyle}
                      >
                        {r.edge}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => st.openPicker('colour', r.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          background: 'transparent',
                          border: '1px solid transparent',
                          borderRadius: 8,
                          padding: '5px 8px',
                          cursor: 'pointer',
                          minHeight: 40,
                          textAlign: 'left',
                        }}
                      >
                        <Swatch code={r.colour} size={22} />
                        <span style={{ fontFamily: 'var(--font-data)', fontSize: 12 }}>
                          {r.colour}
                        </span>
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => st.openPicker('finish', r.id)}
                        style={pickerStyle}
                      >
                        {r.finish}
                      </button>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        inputMode="numeric"
                        autoComplete={NO_AUTOFILL}
                        aria-label="Coats"
                        value={r.coats}
                        onChange={(e) => st.setRow(r.id, { coats: digits(e.target.value) })}
                        style={numStyle}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        inputMode="numeric"
                        autoComplete={NO_AUTOFILL}
                        aria-label="Quantity"
                        value={r.qty}
                        onChange={(e) => st.setRow(r.id, { qty: digits(e.target.value) })}
                        style={{
                          ...numStyle,
                          width: 56,
                          fontWeight: 600,
                          borderColor: 'var(--line)',
                        }}
                      />
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontFamily: 'var(--font-data)',
                        fontSize: 12.5,
                        color: 'var(--ink-muted)',
                      }}
                    >
                      {sqmOf(r).toFixed(2)}
                    </td>
                    <td style={{ paddingRight: 14 }}>
                      <div
                        style={{
                          display: 'flex',
                          gap: 2,
                          justifyContent: 'flex-end',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <button
                          type="button"
                          title="Duplicate this row"
                          aria-label="Duplicate this row"
                          onClick={() => st.duplicateRow(r.id)}
                          style={rowActionStyle('var(--ink-muted)')}
                        >
                          +
                        </button>
                        <button
                          type="button"
                          title="Delete this row"
                          aria-label="Delete this row"
                          onClick={() => st.deleteRow(r.id)}
                          style={rowActionStyle('var(--urgent)')}
                        >
                          ×
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 22,
            padding: 14,
            borderTop: '1px solid var(--line)',
            background: 'var(--ground)',
            flex: 'none',
          }}
        >
          <Total label="Lines" value={`${st.rows.length}`} />
          <Total label="Components" value={`${totalQty}`} />
          <Total label="Sprayed area" value={`${totalArea.toFixed(2)} m²`} />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={cancel}
              style={{ height: 48 }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary blueprint"
              onClick={st.saveItems}
              style={{ height: 48, paddingInline: 18 }}
            >
              Save items
            </button>
          </div>
        </div>
      </div>

      <p style={{ margin: '14px 0 0', fontSize: 13, color: 'var(--ink-muted)', flex: 'none' }}>
        Colour, finish, substrate, component type and edge come from admin reference data and
        pre-fill with the last value used. Numeric fields open a keypad on phone and iPad.
      </p>
    </section>
  );
}

const rowActionStyle = (colour: string): CSSProperties => ({
  width: 30,
  height: 40,
  borderRadius: 8,
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  fontFamily: 'var(--font-data)',
  fontSize: 14,
  color: colour,
});

function Total({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
      <span className="label-xs" style={{ letterSpacing: '0.12em' }}>
        {label}
      </span>
      <span style={{ fontFamily: 'var(--font-data)', fontSize: 14, fontWeight: 500 }}>{value}</span>
    </div>
  );
}
